import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import React from 'react'

import { LocationMetricRow, Order, OrderBy } from '../types'
import {
  formatNumber,
  getRoundedGuestValue,
  getRoundedPrimeCostValue,
} from '../utils'

interface MetricsTableProps {
  data: LocationMetricRow[]
  order: Order
  orderBy: OrderBy
  onRequestSort: (property: OrderBy) => void
  targetVersion: number
  mainLocationCount: number
  otherLocationCount: number
  otherLocationCounts?: number[]
  useNewRounding?: boolean
  startDate?: string
}

const MetricsTable: React.FC<MetricsTableProps> = ({
  data,
  order,
  orderBy,
  onRequestSort,
  targetVersion,
  mainLocationCount,
  otherLocationCount,
  otherLocationCounts = [],
  useNewRounding = false,
  startDate = '',
}) => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // Determine if we should use new metrics (after 2026-04-04)
  const useNewMetrics = startDate >= '2026-04-04'

  // Helper function to get the color based on rounding logic
  const getPrimeCostColor = (
    value: number | undefined,
    defaultValue: number,
    threshold: number,
  ) => {
    const actualValue = value ?? defaultValue
    const compareValue = useNewRounding
      ? getRoundedPrimeCostValue(actualValue)
      : actualValue
    return compareValue >= threshold ? '#d32f2f' : '#2e7d32'
  }

  const getGuestColor = (
    value: number | undefined,
    defaultValue: number,
    threshold: number,
    isPercentage: boolean = true,
  ) => {
    const actualValue = value ?? defaultValue
    const compareValue = useNewRounding
      ? getRoundedGuestValue(actualValue, isPercentage)
      : actualValue
    return compareValue < threshold ? '#d32f2f' : '#2e7d32'
  }

  // Calculate column positions for dividers based on useNewMetrics
  // Before 2026-04-04: 2 (Store) | 5 (Sales) | 9 (Prime Costs: 4 cols) | 13 (Guests: 4 cols) | 17 (Op Bonus)
  // After 2026-04-04:  2 (Store) | 5 (Sales) | 7 (Prime Costs: 2 cols) | 12 (Guests: 5 cols) | 16 (Op Bonus)
  const dividerPositions = useNewMetrics
    ? {
        afterStore: 2,
        afterSales: 5,
        afterPrimeCosts: 7,
        afterGuests: 12,
        afterOpBonus: 16,
      }
    : {
        afterStore: 2,
        afterSales: 5,
        afterPrimeCosts: 9,
        afterGuests: 13,
        afterOpBonus: 17,
      }

  return (
    <TableContainer>
      <Table
        size="small"
        sx={{
          '& .MuiTableCell-root': { padding: '4px 6px' },
          '& .MuiTableCell-head': {
            whiteSpace: 'normal',
            lineHeight: '1.2',
          },
          '& tr:nth-of-type(odd)': {
            backgroundColor: '#f5f5f5',
          },

          // Add vertical dividers between sections
          '& tr:first-child > th': {
            borderRight: '2px solid #e0e0e0',
          },
          '& tr:first-child > th:last-child': {
            borderRight: 'none',
          },
          [`& th:nth-of-type(${dividerPositions.afterStore}), & td:nth-of-type(${dividerPositions.afterStore})`]:
            {
              borderRight: '2px solid #e0e0e0',
            },
          [`& th:nth-of-type(${dividerPositions.afterSales}), & td:nth-of-type(${dividerPositions.afterSales})`]:
            {
              borderRight: '2px solid #e0e0e0',
            },
          [`& th:nth-of-type(${dividerPositions.afterPrimeCosts}), & td:nth-of-type(${dividerPositions.afterPrimeCosts})`]:
            {
              borderRight: '2px solid #e0e0e0',
            },
          [`& th:nth-of-type(${dividerPositions.afterGuests}), & td:nth-of-type(${dividerPositions.afterGuests})`]:
            {
              borderRight: '2px solid #e0e0e0',
            },
          [`& th:nth-of-type(${dividerPositions.afterOpBonus}), & td:nth-of-type(${dividerPositions.afterOpBonus})`]:
            {
              borderRight: '2px solid #e0e0e0',
            },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell colSpan={2}></TableCell>
            <TableCell
              colSpan={3}
              align="center"
              sx={{ padding: '4px 6px', fontFamily: 'Lexend-Regular' }}
            >
              Sales
            </TableCell>
            <TableCell
              colSpan={useNewMetrics ? 2 : 4}
              align="center"
              sx={{ padding: '4px 6px', fontFamily: 'Lexend-Regular' }}
            >
              Prime Costs
            </TableCell>
            <TableCell
              colSpan={useNewMetrics ? 5 : 4}
              align="center"
              sx={{ padding: '4px 6px', fontFamily: 'Lexend-Regular' }}
            >
              Guests
            </TableCell>
            <TableCell
              colSpan={4}
              align="center"
              sx={{ padding: '4px 6px', fontFamily: 'Lexend-Regular' }}
            >
              Operator Bonus
            </TableCell>
            <TableCell
              colSpan={4}
              align="center"
              sx={{ padding: '4px 6px', fontFamily: 'Lexend-Regular' }}
            >
              Manager Bonus (Each)
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell
              align="right"
              sx={{
                width: isMobile ? '20%' : '16%',
                whiteSpace: 'normal',
                padding: '4px 6px',
                fontFamily: 'Lexend-Regular',
              }}
            >
              <TableSortLabel
                active={orderBy === 'bonus_attainment_percent_quarter'}
                direction={
                  orderBy === 'bonus_attainment_percent_quarter' ? order : 'asc'
                }
                onClick={() =>
                  onRequestSort('bonus_attainment_percent_quarter')
                }
              >
                Attainment
              </TableSortLabel>
            </TableCell>
            <TableCell
              sx={{
                width: isMobile ? '25%' : '18%',
                whiteSpace: 'normal',
                minWidth: '180px',
                padding: '4px 6px',
                fontFamily: 'Lexend-Regular',
              }}
            >
              <TableSortLabel
                active={orderBy === 'location'}
                direction={orderBy === 'location' ? order : 'asc'}
                onClick={() => onRequestSort('location')}
              >
                Store
              </TableSortLabel>
            </TableCell>
            <TableCell
              align="right"
              sx={{
                width: isMobile ? '20%' : '16%',
                whiteSpace: 'normal',
                padding: '4px 6px',
                fontFamily: 'Lexend-Regular',
              }}
            >
              <TableSortLabel
                active={orderBy === 'net_sales'}
                direction={orderBy === 'net_sales' ? order : 'asc'}
                onClick={() => onRequestSort('net_sales')}
              >
                QTD Sales
              </TableSortLabel>
            </TableCell>
            <TableCell
              align="right"
              sx={{
                width: isMobile ? '20%' : '16%',
                whiteSpace: 'normal',
                padding: '4px 6px',
                fontFamily: 'Lexend-Regular',
              }}
            >
              <TableSortLabel
                active={orderBy === 'average_weekly_sales_quarter'}
                direction={
                  orderBy === 'average_weekly_sales_quarter' ? order : 'asc'
                }
                onClick={() => onRequestSort('average_weekly_sales_quarter')}
              >
                Average Weekly Sales
              </TableSortLabel>
            </TableCell>
            <TableCell
              align="right"
              sx={{
                width: isMobile ? '20%' : '16%',
                whiteSpace: 'normal',
                padding: '4px 6px',
                fontFamily: 'Lexend-Regular',
              }}
            >
              <TableSortLabel
                active={orderBy === 'bonus_sales_to_next_band'}
                direction={
                  orderBy === 'bonus_sales_to_next_band' ? order : 'asc'
                }
                onClick={() => onRequestSort('bonus_sales_to_next_band')}
              >
                Weekly Sales to Next Band
              </TableSortLabel>
            </TableCell>
            {useNewMetrics ? (
              <>
                <TableCell
                  align="right"
                  sx={{
                    width: isMobile ? '20%' : '16%',
                    whiteSpace: 'normal',
                    padding: '4px 6px',
                    fontFamily: 'Lexend-Regular',
                  }}
                >
                  <TableSortLabel
                    active={orderBy === 'large_meat_variance'}
                    direction={
                      orderBy === 'large_meat_variance' ? order : 'asc'
                    }
                    onClick={() => onRequestSort('large_meat_variance')}
                  >
                    Large Meat Variance
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    width: isMobile ? '20%' : '16%',
                    whiteSpace: 'normal',
                    padding: '4px 6px',
                    fontFamily: 'Lexend-Regular',
                  }}
                >
                  <TableSortLabel
                    active={orderBy === 'labor_variance'}
                    direction={orderBy === 'labor_variance' ? order : 'asc'}
                    onClick={() => onRequestSort('labor_variance')}
                  >
                    Labor Variance
                  </TableSortLabel>
                </TableCell>
              </>
            ) : (
              <>
                <TableCell
                  align="right"
                  sx={{
                    width: isMobile ? '20%' : '16%',
                    whiteSpace: 'normal',
                    padding: '4px 6px',
                    fontFamily: 'Lexend-Regular',
                  }}
                >
                  <TableSortLabel
                    active={orderBy === 'food_and_beverage_variance'}
                    direction={
                      orderBy === 'food_and_beverage_variance' ? order : 'asc'
                    }
                    onClick={() => onRequestSort('food_and_beverage_variance')}
                  >
                    Food Variance
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    width: isMobile ? '20%' : '16%',
                    whiteSpace: 'normal',
                    padding: '4px 6px',
                    fontFamily: 'Lexend-Regular',
                  }}
                >
                  <TableSortLabel
                    active={orderBy === 'paper_variance'}
                    direction={orderBy === 'paper_variance' ? order : 'asc'}
                    onClick={() => onRequestSort('paper_variance')}
                  >
                    Paper Variance
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    width: isMobile ? '20%' : '16%',
                    whiteSpace: 'normal',
                    padding: '4px 6px',
                    fontFamily: 'Lexend-Regular',
                  }}
                >
                  <TableSortLabel
                    active={orderBy === 'labor_variance'}
                    direction={orderBy === 'labor_variance' ? order : 'asc'}
                    onClick={() => onRequestSort('labor_variance')}
                  >
                    Labor Variance
                  </TableSortLabel>
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    width: isMobile ? '20%' : '16%',
                    whiteSpace: 'normal',
                    padding: '4px 6px',
                    fontFamily: 'Lexend-Regular',
                  }}
                >
                  <TableSortLabel
                    active={orderBy === 'total_prime_cost_variance'}
                    direction={
                      orderBy === 'total_prime_cost_variance' ? order : 'asc'
                    }
                    onClick={() => onRequestSort('total_prime_cost_variance')}
                  >
                    Total Variance
                  </TableSortLabel>
                </TableCell>
              </>
            )}
            <TableCell
              align="right"
              sx={{
                width: isMobile ? '20%' : '16%',
                whiteSpace: 'normal',
                padding: '4px 6px',
                fontFamily: 'Lexend-Regular',
              }}
            >
              <TableSortLabel
                active={orderBy === 'qualtrics_osat_percent'}
                direction={orderBy === 'qualtrics_osat_percent' ? order : 'asc'}
                onClick={() => onRequestSort('qualtrics_osat_percent')}
              >
                OSAT
              </TableSortLabel>
            </TableCell>
            <TableCell
              align="right"
              sx={{
                width: isMobile ? '20%' : '16%',
                whiteSpace: 'normal',
                padding: '4px 6px',
                fontFamily: 'Lexend-Regular',
              }}
            >
              <TableSortLabel
                active={orderBy === 'google_rating'}
                direction={orderBy === 'google_rating' ? order : 'asc'}
                onClick={() => onRequestSort('google_rating')}
              >
                Google
              </TableSortLabel>
            </TableCell>
            <TableCell
              align="right"
              sx={{
                width: isMobile ? '20%' : '16%',
                whiteSpace: 'normal',
                padding: '4px 6px',
                fontFamily: 'Lexend-Regular',
              }}
            >
              <TableSortLabel
                active={
                  orderBy === 'delivery_avg_customer_review_score_doordash'
                }
                direction={
                  orderBy === 'delivery_avg_customer_review_score_doordash'
                    ? order
                    : 'asc'
                }
                onClick={() =>
                  onRequestSort('delivery_avg_customer_review_score_doordash')
                }
              >
                Delivery
              </TableSortLabel>
            </TableCell>
            <TableCell
              align="right"
              sx={{
                width: isMobile ? '20%' : '16%',
                whiteSpace: 'normal',
                padding: '4px 6px',
                fontFamily: 'Lexend-Regular',
              }}
            >
              <TableSortLabel
                active={orderBy === 'hme_goal_percent'}
                direction={orderBy === 'hme_goal_percent' ? order : 'asc'}
                onClick={() => onRequestSort('hme_goal_percent')}
              >
                {useNewMetrics ? 'SOS (HME)' : 'SOS Attainment'}
              </TableSortLabel>
            </TableCell>
            {useNewMetrics && (
              <TableCell
                align="right"
                sx={{
                  width: isMobile ? '20%' : '16%',
                  whiteSpace: 'normal',
                  padding: '4px 6px',
                  fontFamily: 'Lexend-Regular',
                }}
              >
                <TableSortLabel
                  active={orderBy === 'qualtrics_drive_thru_wait_time_percent'}
                  direction={
                    orderBy === 'qualtrics_drive_thru_wait_time_percent'
                      ? order
                      : 'asc'
                  }
                  onClick={() =>
                    onRequestSort('qualtrics_drive_thru_wait_time_percent')
                  }
                >
                  SOS (Qualtrics DT)
                </TableSortLabel>
              </TableCell>
            )}
            <TableCell
              align="right"
              sx={{
                width: isMobile ? '20%' : '16%',
                whiteSpace: 'normal',
                padding: '4px 6px',
                fontFamily: 'Lexend-Regular',
              }}
            >
              <TableSortLabel
                active={orderBy === 'bonus_op_prime_cost_quarter'}
                direction={
                  orderBy === 'bonus_op_prime_cost_quarter' ? order : 'asc'
                }
                onClick={() => onRequestSort('bonus_op_prime_cost_quarter')}
              >
                Prime Cost Bonus
              </TableSortLabel>
            </TableCell>
            <TableCell
              align="right"
              sx={{
                width: isMobile ? '20%' : '16%',
                whiteSpace: 'normal',
                padding: '4px 6px',
                fontFamily: 'Lexend-Regular',
              }}
            >
              <TableSortLabel
                active={orderBy === 'bonus_op_guest_metric_quarter'}
                direction={
                  orderBy === 'bonus_op_guest_metric_quarter' ? order : 'asc'
                }
                onClick={() => onRequestSort('bonus_op_guest_metric_quarter')}
              >
                Guest Bonus
              </TableSortLabel>
            </TableCell>
            <TableCell
              align="right"
              sx={{
                width: isMobile ? '20%' : '16%',
                whiteSpace: 'normal',
                padding: '4px 6px',
                fontFamily: 'Lexend-Semibold',
              }}
            >
              <TableSortLabel
                active={orderBy === 'bonus_op_total_quarter'}
                direction={orderBy === 'bonus_op_total_quarter' ? order : 'asc'}
                onClick={() => onRequestSort('bonus_op_total_quarter')}
              >
                Total Bonus
              </TableSortLabel>
            </TableCell>
            <TableCell
              align="right"
              sx={{
                width: isMobile ? '20%' : '16%',
                whiteSpace: 'normal',
                padding: '4px 6px',
                fontFamily: 'Lexend-Regular',
              }}
            >
              <TableSortLabel
                active={orderBy === 'bonus_op_missed_opportunity_quarter'}
                direction={
                  orderBy === 'bonus_op_missed_opportunity_quarter'
                    ? order
                    : 'asc'
                }
                onClick={() =>
                  onRequestSort('bonus_op_missed_opportunity_quarter')
                }
              >
                Missed Bonus
              </TableSortLabel>
            </TableCell>
            <TableCell
              align="right"
              sx={{
                width: isMobile ? '20%' : '16%',
                whiteSpace: 'normal',
                padding: '4px 6px',
                fontFamily: 'Lexend-Regular',
              }}
            >
              <TableSortLabel
                active={orderBy === 'bonus_manager_prime_cost_quarter'}
                direction={
                  orderBy === 'bonus_manager_prime_cost_quarter' ? order : 'asc'
                }
                onClick={() =>
                  onRequestSort('bonus_manager_prime_cost_quarter')
                }
              >
                Prime Cost Bonus
              </TableSortLabel>
            </TableCell>
            <TableCell
              align="right"
              sx={{
                width: isMobile ? '20%' : '16%',
                whiteSpace: 'normal',
                padding: '4px 6px',
                fontFamily: 'Lexend-Regular',
              }}
            >
              <TableSortLabel
                active={orderBy === 'bonus_manager_guest_metric_quarter'}
                direction={
                  orderBy === 'bonus_manager_guest_metric_quarter'
                    ? order
                    : 'asc'
                }
                onClick={() =>
                  onRequestSort('bonus_manager_guest_metric_quarter')
                }
              >
                Guest Bonus
              </TableSortLabel>
            </TableCell>
            <TableCell
              align="right"
              sx={{
                width: isMobile ? '20%' : '16%',
                whiteSpace: 'normal',
                padding: '4px 6px',
                fontFamily: 'Lexend-Semibold',
              }}
            >
              <TableSortLabel
                active={orderBy === 'bonus_manager_total_quarter'}
                direction={
                  orderBy === 'bonus_manager_total_quarter' ? order : 'asc'
                }
                onClick={() => onRequestSort('bonus_manager_total_quarter')}
              >
                Total Bonus
              </TableSortLabel>
            </TableCell>
            <TableCell
              align="right"
              sx={{
                width: isMobile ? '20%' : '16%',
                whiteSpace: 'normal',
                padding: '4px 6px',
                fontFamily: 'Lexend-Regular',
              }}
            >
              <TableSortLabel
                active={orderBy === 'bonus_manager_missed_opportunity_quarter'}
                direction={
                  orderBy === 'bonus_manager_missed_opportunity_quarter'
                    ? order
                    : 'asc'
                }
                onClick={() =>
                  onRequestSort('bonus_manager_missed_opportunity_quarter')
                }
              >
                Missed Bonus
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row) => {
            // Calculate location count based on datasetId for proper averaging
            let locationCount = 1
            if (row.locationId < 0) {
              if (row.locationId === -1) {
                // Main dataset summary
                locationCount = mainLocationCount
              } else {
                // Other market leader summary - use datasetId to find the right count
                // datasetId 2 -> otherLocationCounts[0], datasetId 3 -> otherLocationCounts[1], etc.
                const otherIndex = (row.datasetId || 2) - 2
                locationCount =
                  otherLocationCounts[otherIndex] ?? otherLocationCount
              }
            }

            return (
              <TableRow
                key={row.locationId}
                sx={
                  row.locationId < 0
                    ? {
                        backgroundColor: '#f0f8ff!important',
                        borderTop: '2px solid #1976d2',
                        '& > td': {
                          fontFamily: 'Lexend-SemiBold',
                        },
                      }
                    : undefined
                }
              >
                <TableCell
                  align="right"
                  sx={{
                    fontFamily: 'Lexend-Regular',
                    color:
                      (row.bonusAttainment?.value ?? 0) >= 0.9
                        ? '#2e7d32'
                        : (row.bonusAttainment?.value ?? 0) >= 0.8
                        ? '#ed6c02'
                        : '#d32f2f',
                  }}
                >
                  {row.bonusAttainment
                    ? formatNumber(row.bonusAttainment.value, false, true, 1)
                    : '-'}
                </TableCell>
                <TableCell
                  sx={{
                    whiteSpace: 'normal',
                    minWidth: '180px',
                    maxWidth: isMobile ? '180px' : '250px',
                    fontFamily: 'Lexend-Regular',
                    color:
                      (row.bonusAttainment?.value ?? 0) >= 0.9
                        ? '#2e7d32'
                        : (row.bonusAttainment?.value ?? 0) >= 0.8
                        ? '#ed6c02'
                        : '#d32f2f',
                  }}
                >
                  {row.locationId < 0
                    ? row.locationName
                    : `${row.locationCode} - ${row.locationName}`}
                </TableCell>
                <TableCell align="right" sx={{ fontFamily: 'Lexend-Regular' }}>
                  {row.netSales
                    ? formatNumber(row.netSales.value, true, false, 0)
                    : '-'}
                </TableCell>
                <TableCell align="right" sx={{ fontFamily: 'Lexend-Regular' }}>
                  {row.avgWeeklySales
                    ? formatNumber(
                        row.avgWeeklySales.value / locationCount,
                        true,
                        false,
                        0,
                      )
                    : '-'}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontFamily: 'Lexend-Regular',
                    color:
                      (row.bonusSalesToNextBand?.value ?? 0) < 50000
                        ? '#2e7d32'
                        : (row.bonusSalesToNextBand?.value ?? 0) <= 150000
                        ? '#ed6c02'
                        : '#d32f2f',
                  }}
                >
                  {row.bonusSalesToNextBand
                    ? formatNumber(
                        row.bonusSalesToNextBand.value / locationCount,
                        true,
                        false,
                        0,
                      )
                    : '-'}
                </TableCell>
                {useNewMetrics ? (
                  <>
                    <TableCell
                      align="right"
                      sx={{
                        fontFamily: 'Lexend-Regular',
                        color: getPrimeCostColor(
                          row.foodVariance?.value,
                          0,
                          0.0025,
                        ),
                      }}
                    >
                      {row.foodVariance
                        ? formatNumber(
                            row.foodVariance.value,
                            false,
                            true,
                            2,
                            null,
                          )
                        : '-'}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontFamily: 'Lexend-Regular',
                        color: getPrimeCostColor(
                          row.laborVariance?.value,
                          0.05,
                          0.0025,
                        ),
                      }}
                    >
                      {row.laborVariance
                        ? formatNumber(
                            row.laborVariance.value,
                            false,
                            true,
                            useNewRounding ? undefined : 2,
                            useNewRounding ? 'primeCost' : null,
                          )
                        : '-'}
                    </TableCell>
                  </>
                ) : (
                  <>
                    <TableCell
                      align="right"
                      sx={{
                        fontFamily: 'Lexend-Regular',
                        color: getPrimeCostColor(
                          row.foodVariance?.value,
                          0.075,
                          0.0075,
                        ),
                      }}
                    >
                      {row.foodVariance
                        ? formatNumber(
                            row.foodVariance.value,
                            false,
                            true,
                            useNewRounding ? undefined : 2,
                            useNewRounding ? 'primeCost' : null,
                          )
                        : '-'}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontFamily: 'Lexend-Regular',
                        color: getPrimeCostColor(
                          row.paperVariance?.value,
                          0.025,
                          0.0025,
                        ),
                      }}
                    >
                      {row.paperVariance
                        ? formatNumber(
                            row.paperVariance.value,
                            false,
                            true,
                            useNewRounding ? undefined : 2,
                            useNewRounding ? 'primeCost' : null,
                          )
                        : '-'}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontFamily: 'Lexend-Regular',
                        color: getPrimeCostColor(
                          row.laborVariance?.value,
                          0.05,
                          0.005,
                        ),
                      }}
                    >
                      {row.laborVariance
                        ? formatNumber(
                            row.laborVariance.value,
                            false,
                            true,
                            useNewRounding ? undefined : 2,
                            useNewRounding ? 'primeCost' : null,
                          )
                        : '-'}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        fontFamily: 'Lexend-Regular',
                        color: getPrimeCostColor(
                          row.totalVariance?.value,
                          targetVersion === 0 ? 0.015 : 0.01,
                          targetVersion === 0 ? 0.015 : 0.01,
                        ),
                      }}
                    >
                      {row.totalVariance
                        ? formatNumber(
                            row.totalVariance.value,
                            false,
                            true,
                            useNewRounding ? undefined : 2,
                            useNewRounding ? 'primeCost' : null,
                          )
                        : '-'}
                    </TableCell>
                  </>
                )}
                <TableCell
                  align="right"
                  sx={{
                    fontFamily: 'Lexend-Regular',
                    color: getGuestColor(
                      row.osat?.value,
                      0,
                      targetVersion >= 2 ? 0.7 : 0.75,
                      true,
                    ),
                  }}
                >
                  {row.osat
                    ? formatNumber(
                        row.osat.value,
                        false,
                        true,
                        useNewRounding ? undefined : 2,
                        useNewRounding ? 'guest' : null,
                      )
                    : '-'}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontFamily: 'Lexend-Regular',
                    color: getGuestColor(
                      row.google?.value,
                      0,
                      targetVersion === 0 || targetVersion >= 2 ? 4 : 4.2,
                      false,
                    ),
                  }}
                >
                  {row.google
                    ? formatNumber(
                        row.google.value,
                        false,
                        false,
                        useNewRounding ? undefined : 2,
                        useNewRounding ? 'guest' : null,
                      )
                    : '-'}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontFamily: 'Lexend-Regular',
                    color: getGuestColor(
                      row.delivery?.value,
                      0,
                      targetVersion === 0 || targetVersion === 2
                        ? 4.0
                        : targetVersion === 3
                        ? 3.5
                        : 4.2,
                      false,
                    ),
                  }}
                >
                  {row.delivery
                    ? formatNumber(
                        row.delivery.value,
                        false,
                        false,
                        useNewRounding ? undefined : 2,
                        useNewRounding ? 'guest' : null,
                      )
                    : '-'}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontFamily: 'Lexend-Regular',
                    color: getGuestColor(
                      row.sosAttainment?.value,
                      0,
                      targetVersion === 0 ? 0.75 : 0.7,
                      true,
                    ),
                  }}
                >
                  {row.sosAttainment
                    ? formatNumber(
                        row.sosAttainment.value,
                        false,
                        true,
                        useNewRounding ? undefined : 2,
                        useNewRounding ? 'guest' : null,
                      )
                    : '-'}
                </TableCell>
                {useNewMetrics && (
                  <TableCell
                    align="right"
                    sx={{
                      fontFamily: 'Lexend-Regular',
                      color: getGuestColor(
                        row.sosQualtricsAttainment?.value,
                        0,
                        0.7,
                        true,
                      ),
                    }}
                  >
                    {row.sosQualtricsAttainment
                      ? formatNumber(
                          row.sosQualtricsAttainment.value,
                          false,
                          true,
                          useNewRounding ? undefined : 2,
                          useNewRounding ? 'guest' : null,
                        )
                      : '-'}
                  </TableCell>
                )}
                <TableCell
                  align="right"
                  sx={{
                    fontFamily: 'Lexend-Regular',
                    color:
                      (row.opPrimeCostBonus?.value ?? 0) === 0
                        ? '#d32f2f'
                        : 'inherit',
                  }}
                >
                  {row.opPrimeCostBonus
                    ? formatNumber(row.opPrimeCostBonus.value, true, false, 0)
                    : '-'}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontFamily: 'Lexend-Regular',
                    color:
                      (row.opGuestBonus?.value ?? 0) === 0
                        ? '#d32f2f'
                        : 'inherit',
                  }}
                >
                  {row.opGuestBonus
                    ? formatNumber(row.opGuestBonus.value, true, false, 0)
                    : '-'}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontFamily: 'Lexend-SemiBold',
                    color:
                      (row.opTotalBonus?.value ?? 0) === 0
                        ? '#d32f2f'
                        : 'inherit',
                  }}
                >
                  {row.opTotalBonus
                    ? formatNumber(row.opTotalBonus.value, true, false, 0)
                    : '-'}
                </TableCell>
                <TableCell align="right" sx={{ fontFamily: 'Lexend-Regular' }}>
                  {row.opMissedOpportunity
                    ? formatNumber(
                        row.opMissedOpportunity.value,
                        true,
                        false,
                        0,
                      )
                    : '-'}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontFamily: 'Lexend-Regular',
                    color:
                      (row.managerPrimeCostBonus?.value ?? 0) === 0
                        ? '#d32f2f'
                        : 'inherit',
                  }}
                >
                  {row.managerPrimeCostBonus
                    ? formatNumber(
                        row.managerPrimeCostBonus.value / 2,
                        true,
                        false,
                        0,
                      )
                    : '-'}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontFamily: 'Lexend-Regular',
                    color:
                      (row.managerGuestBonus?.value ?? 0) === 0
                        ? '#d32f2f'
                        : 'inherit',
                  }}
                >
                  {row.managerGuestBonus
                    ? formatNumber(
                        row.managerGuestBonus.value / 2,
                        true,
                        false,
                        0,
                      )
                    : '-'}
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    fontFamily: 'Lexend-SemiBold',
                    color:
                      (row.managerTotalBonus?.value ?? 0) === 0
                        ? '#d32f2f'
                        : 'inherit',
                  }}
                >
                  {row.managerTotalBonus
                    ? formatNumber(
                        row.managerTotalBonus.value / 2,
                        true,
                        false,
                        0,
                      )
                    : '-'}
                </TableCell>
                <TableCell align="right" sx={{ fontFamily: 'Lexend-Regular' }}>
                  {row.managerMissedOpportunity
                    ? formatNumber(
                        row.managerMissedOpportunity.value / 2,
                        true,
                        false,
                        0,
                      )
                    : '-'}
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

export default MetricsTable
